import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useState, useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage';

const DEFAULT_CARD_FIELDS = ['full_name', 'company', 'email', 'phone', 'action_date', 'created_at', 'assigned_to'];

export const KanbanCard = ({
  lead,
  isDragging = false,
  onOpenDetail,
  onDelete,
  selectionMode = false,
  isSelected = false,
  onToggleSelection
}) => {
  const { t } = useLanguage();
  const [visibleFields, setVisibleFields] = useState(DEFAULT_CARD_FIELDS);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: lead.id, disabled: selectionMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  };

  useEffect(() => {
    const savedFields = localStorage.getItem('kanban_card_fields');
    if (savedFields) {
      setVisibleFields(JSON.parse(savedFields));
    }
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes} ${day}/${month}/${year}`;
  };

  const shouldShow = (field) => visibleFields.includes(field);

  const getFieldValue = (field) => {
    if (field === 'full_name') return lead.full_name || lead.field_data?.full_name;
    if (field === 'company') return lead.company || lead.field_data?.company;
    if (field === 'email') return lead.email || lead.field_data?.email;
    if (field === 'phone') return lead.phone || lead.field_data?.phone;
    if (field === 'action_date') return lead.action_date;
    if (field === 'created_at') return lead.created_at;
    if (field === 'assigned_to') return lead.assigned_to;
    return lead.field_data?.[field];
  };

  const getFieldLabel = (field) => {
    const labelMap = {
      full_name: t('fullNameLabel'),
      phone: t('phoneLabel'),
      message: t('messageLabel'),
      action_date: t('actionDate'),
      ad_name: t('adName'),
      campaign_name: t('campaignName'),
      adset_name: t('adsetName'),
      form_id: t('formId'),
      ad_id: t('adId'),
      campaign_id: t('campaignId'),
      fb_lead_id: t('fbLeadId'),
      created_time: t('createdTime'),
      created_at: t('createdDate'),
      assigned_to: t('assignedToLabel'),
      source: t('sourceLabel'),
    };
    return labelMap[field] || field.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Fields that have special rendering
  const specialFields = ['full_name', 'phone', 'action_date', 'created_at', 'assigned_to'];

  // Get other fields that should be shown
  const otherFields = visibleFields.filter(f =>
    !specialFields.includes(f) && getFieldValue(f)
  );

  const handleCardClick = (e) => {
    if (selectionMode) {
      e.stopPropagation();
      onToggleSelection(lead.id);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(!selectionMode && attributes)}
      {...(!selectionMode && listeners)}
      onClick={handleCardClick}
      className={`
        bg-white rounded-lg p-4 shadow-sm border-2
        hover:shadow-md transition-all
        ${selectionMode ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'}
        ${isDragging ? 'shadow-xl rotate-3' : ''}
        ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
      `}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 flex-1">
          {selectionMode && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelection(lead.id)}
              onClick={(e) => e.stopPropagation()}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          )}
          <h4 className="font-semibold text-gray-900">
            {getFieldValue('full_name') || t('unnamedLead')}
          </h4>
          {lead.hasDuplicate && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800"
              title={t('duplicateWarning')}
            >
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {t('duplicate')}
            </span>
          )}
        </div>
        {!selectionMode && (
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onOpenDetail) {
                  onOpenDetail(lead);
                }
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              className="text-gray-400 hover:text-blue-600 transition-colors pointer-events-auto"
              title={t('viewDetails')}
            >
              <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onDelete) {
                  onDelete(lead);
                }
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              className="text-gray-400 hover:text-red-600 transition-colors pointer-events-auto"
              title={t('deleteLead')}
            >
              <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        )}
      </div>

      <div className="space-y-1 mb-3">
        {shouldShow('phone') && getFieldValue('phone') && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <span>{getFieldValue('phone')}</span>
          </div>
        )}

        {/* Render other dynamic fields */}
        {otherFields.map(field => (
          <p key={field} className="text-sm text-gray-600">
            <span className="font-medium">{getFieldLabel(field)}:</span> {String(getFieldValue(field))}
          </p>
        ))}
      </div>

      {/* Event Badge - shown prominently if assigned to event */}
      {lead.event_name && (
        <div className="mb-3 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-purple-600" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-xs font-medium text-purple-800">{t('event') || 'Event'}</p>
              <p className="text-sm font-semibold text-purple-900">{lead.event_name}</p>
              {lead.event_type && (
                <p className="text-xs text-purple-700">{lead.event_type}</p>
              )}
              {lead.event_location && (
                <p className="text-xs text-purple-700">{lead.event_location}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preferred Days Tags */}
      {lead.field_data?.preferred_days && lead.field_data.preferred_days.length > 0 && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-1">
            {lead.field_data.preferred_days.map((day) => (
              <span
                key={day}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200"
              >
                {t(day)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Action Date - shown prominently if set */}
      {lead.action_date && (
        <div className="mb-3 flex items-center gap-2 text-blue-600">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="text-sm font-medium">{formatDateTime(lead.action_date)}</span>
        </div>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        {shouldShow('created_at') && (
          <span className="text-xs text-gray-500">
            {getFieldValue('created_at') ? formatDate(getFieldValue('created_at')) : t('noDate')}
          </span>
        )}

        {shouldShow('assigned_to') && getFieldValue('assigned_to') && (
          <div className="flex items-center gap-1">
            <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-medium">
              {getFieldValue('assigned_to').charAt(0).toUpperCase()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
